import { useCallback, useEffect, useState } from "react";
import { DeviceTypes, UserRole } from "@repo/database";
import { v4 as uuidv4 } from "uuid";

import { usePaymentMethod } from "~/hooks/use-payment-method";
import { usePlatform } from "~/hooks/use-platform";
import { useUser } from "~/hooks/use-user";
import { api } from "~/lib/api";
import { eventBus } from "~/lib/eventbus";
import { useBookingManager } from "~/managers/use-booking-manager";
import { useDeviceStore } from "~/stores/use-device-store";

type MessagePayload =
  | {
      action: "set-push-token";
      payload: {
        deviceId: string;
        token: string;
      };
    }
  | {
      action: "push-notification-data";
      payload: {
        event: "open-booking" | "open-online-order" | "open-order";
        data: unknown;
      };
    }
  | {
      action: "softpos";
      payload: {
        enabled: boolean;
      };
    }
  | {
      action: "mollie-t2p";
      payload: {
        enabled: boolean;
      };
    }
  | {
      action: "softpos-paid";
      payload: {
        orderId: string;
        reference: string;
        cardholderTicket?: string;
      };
    }
  | {
      action: "softpos-status";
      payload: {
        orderId: string;
        reference: string;
        cardholderTicket?: string;
        status: "PAID" | "CANCEL" | "CHANGE";
      };
    }
  | {
      action: "mollie-t2p-result";
      payload: any;
    }
  | {
      action: "mollie-t2p-enabled";
      payload: {
        enabled: boolean;
      };
    }
  | {
      action: "set-branch";
      payload: {
        branch: string;
      };
    };

export function NativeProvider() {
  const { mutate: connectDevice } = api.device.connectDevice.useMutation();

  const { platform } = usePlatform();

  const { user } = useUser();

  const { setPushToken, setDeviceId, pushToken, deviceId } = useDeviceStore();

  const { openBookingById } = useBookingManager();

  const { setSoftPosEnabled, setMollieT2PEnabled } = usePaymentMethod();

  const [lastBranchUpdate, setLastBranchUpdate] = useState<number>(0);

  const utils = api.useUtils();

  const { refetch: refetchUser } = api.user.getUser.useQuery(undefined, {
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const onMessageFromRN = useCallback(
    (message: string) => {
      // Parse the message as a generic object and validate the type
      const parsedMessage = JSON.parse(message) as MessagePayload;

      switch (parsedMessage.action) {
        case "set-push-token":
          setPushToken(parsedMessage.payload.token);
          setDeviceId(parsedMessage.payload.deviceId);
          break;
        case "softpos-status":
          eventBus.emit("softpos-status", {
            orderId: parsedMessage.payload.orderId,
            paymentId: parsedMessage.payload.reference,
            cardholderTicket: parsedMessage.payload.cardholderTicket,
            status: parsedMessage.payload.status,
          });
          break;
        case "softpos-paid":
          eventBus.emit("softpos-status", {
            orderId: parsedMessage.payload.orderId,
            paymentId: parsedMessage.payload.reference,
            cardholderTicket: parsedMessage.payload.cardholderTicket,
          });
          break;
        case "mollie-t2p-result":
          console.log(parsedMessage);
          break;
        case "softpos":
          setSoftPosEnabled(parsedMessage.payload.enabled);
          break;
        case "mollie-t2p-enabled":
          setMollieT2PEnabled(parsedMessage.payload.enabled);
          break;
        case "push-notification-data":
          if (parsedMessage.payload.event === "open-booking") {
            void openBookingById(parsedMessage.payload.bookingId);
          }
          break;
        case "set-branch":
          // Prevent rapid branch updates that could cause infinite loops
          const now = Date.now();
          if (now - lastBranchUpdate < 5000) {
            // 5 second cooldown
            console.warn("Branch update ignored due to rate limiting");
            return;
          }
          setLastBranchUpdate(now);

          // Only update if the branch is actually different
          if (parsedMessage.payload.branch !== user.branch) {
            // window.ReactNativeWebView?.postMessage(
            //   JSON.stringify({
            //     event: "set-branch",
            //     data: {
            //       branch: parsedMessage.payload.branch,
            //     },
            //   }),
            // );
          }
          break;
        default:
          // Handle unknown actions if necessary
          console.warn("Unknown action:", parsedMessage);
      }
    },
    [
      openBookingById,
      setDeviceId,
      setMollieT2PEnabled,
      setPushToken,
      setSoftPosEnabled,
      lastBranchUpdate,
      user.branch,
    ],
  );

  useEffect(() => {
    window.onMessageFromRN = onMessageFromRN;
  }, [onMessageFromRN]);

  useEffect(() => {
    if (pushToken && deviceId && user.role !== UserRole.admin) {
      connectDevice(
        {
          deviceId: deviceId,
          pushToken: pushToken,
          type: DeviceTypes.handheld,
          name: "Handheld",
        },
        {
          onSuccess: () => {
            // alert("CONNECTED");
          },
        },
      );
    }
  }, [connectDevice, deviceId, pushToken, user]);

  useEffect(() => {
    if (platform === "ANDROID" || platform === "IOS") {
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({
          event: "request-softpos",
        }),
      );
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({
          event: "request-mollie-t2p",
        }),
      );
    }
  }, [platform]);

  useEffect(() => {
    if (platform === "ANDROID" || platform === "IOS") {
      if (!pushToken) {
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({
            event: "request-push-token",
            data: null,
          }),
        );
      }
    } else {
      setDeviceId(uuidv4());
    }
  }, [platform, pushToken, setDeviceId]);

  // Send branch to React Native when it changes
  useEffect(() => {
    if (platform === "ANDROID" || platform === "IOS") {
      void refetchUser().then((result) => {
        if (result.data) {
          // window.ReactNativeWebView?.postMessage(
          //   JSON.stringify({
          //     event: "set-branch",
          //     data: {
          //       branch: result.data.branch,
          //     },
          //   }),
          // );
        }
      });
    }
  }, [platform, refetchUser]);

  return null;
}
